import { AxiosInstance, CancelTokenSource } from 'axios';
import { getParams } from '../../../core/api/axios';
import { TypeParams } from '../../../core/utils/type-params';
import { Note, SamplingClassification } from '../../../shared/types/notes/notes';
import { NDVIInfo, SatellitalLayersInfo, ZonificationInfo } from '../../../shared/types/satellital/layers';
import {
    RanchVariablesCategorized,
    SatellitalChartStatistics,
    SatellitalValues
} from '../../../shared/types/satellital/evolution_chart';
import {
    ClaimsPort,
    ItemClaimsPort,
    ItemWithClaimsResponse,
    MessageResponse,
    NdviDiffRequest,
    SatellitalImagesResponse,
    ZonificationRequest
} from '../../../core/api/ports';
import { TenantApi } from '../../types';
import { RanchItemApi } from '../../shared/api/RanchItemApi';
import { ItemClaimsUrls } from './zurich-urls';

/**
 * El backend de Zurich puede mandar `value.mean` anidado (`{ mean: { mean: number, ... } }`) en la serie de
 * `/ranch/satellital/images/` (`values`). El gráfico espera un número: se aplana acá, en el adaptador
 * (antes lo hacía `SatellitalEvolutionChart`, fix "arreglo visualizacion grafica" del repo de Zurich).
 */
export const normalizeSatellitalValues = (values: SatellitalValues): SatellitalValues => {
    if (!values || typeof values !== 'object') {
        return values;
    }
    const normalized: any = {};
    Object.keys(values).forEach(bandKey => {
        const bandRows = values[bandKey];
        normalized[bandKey] = Array.isArray(bandRows)
            ? bandRows.map((item: any) => {
                  if (!item || !item.value) return item;
                  const rawMean = item.value.mean;
                  const mean =
                      rawMean && typeof rawMean === 'object' && typeof rawMean.mean === 'number'
                          ? rawMean.mean
                          : rawMean;
                  return {
                      ...item,
                      value: {
                          ...item.value,
                          mean
                      }
                  };
              })
            : bandRows;
    });
    return normalized;
};

/**
 * Adaptador del backend de Zurich (acceso por token: varios endpoints no reciben id porque el backend resuelve el
 * item a partir del token). Implementa `ClaimsPort` e `ItemClaimsPort` con los mismos requests que
 * `ItemWithSinistersApi` del repo de Zurich.
 */
export class ZurichApi implements ClaimsPort, ItemClaimsPort {
    constructor(private readonly http: AxiosInstance) {}

    //Detalle de item con lista de siniestros
    getItemWithClaims(): Promise<ItemWithClaimsResponse> {
        return this.http.get(`${ItemClaimsUrls.DETAIL_INFO}`).then(response => response.data);
    }

    getSamplingClassifications(claimId: number): Promise<{ sampling_classifications: SamplingClassification[] }> {
        return this.http
            .get(`${ItemClaimsUrls.SAMPLING_CLASSIFICATION}`, {
                params: getParams([{ key: TypeParams.ID, value: claimId }])
            })
            .then(response => response.data);
    }

    //Notas de un siniestro
    getSinisterNotes(id: number): Promise<{ ranch_notes: Note[] }> {
        return this.http
            .get(`${ItemClaimsUrls.SINISTER_NOTES}`, {
                params: getParams([{ key: TypeParams.ID, value: id }])
            })
            .then(response => response.data);
    }

    //Agregar nota a un siniestro
    postNote(claimId: number, note: Note): Promise<MessageResponse> {
        return this.http
            .post(`${ItemClaimsUrls.SINISTER_NOTES}`, {
                claim_id: claimId,
                ...note
            })
            .then(response => response.data);
    }

    // El id viaja armado en la URL (igual que en el repo de Zurich).
    patchNote(note: Note): Promise<MessageResponse> {
        const url = `${ItemClaimsUrls.SINISTER_NOTES}?${TypeParams.ID}=${note.id}`;
        return this.http
            .patch(url, {
                ...note
            })
            .then(response => response.data);
    }

    // El id viaja en la URL y también en `params` (se mantiene tal cual el request original).
    deleteNote(id: number): Promise<any> {
        const url = `${ItemClaimsUrls.SINISTER_NOTES}?${TypeParams.ID}=${id}`;
        return this.http
            .delete(url, {
                params: getParams([{ key: TypeParams.ID, value: id }])
            })
            .then(response => response.data);
    }

    getSatellitalImages(id: number): Promise<SatellitalImagesResponse> {
        return this.http
            .get(`${ItemClaimsUrls.SATELLITAL_IMAGES}`, {
                params: getParams([{ key: TypeParams.ID, value: id }])
            })
            .then(response => response.data)
            .then((data: SatellitalImagesResponse) =>
                data ? { ...data, values: normalizeSatellitalValues(data.values) } : data
            );
    }

    getSatellitalLayers(id: number): Promise<SatellitalLayersInfo> {
        return this.http
            .get(`${ItemClaimsUrls.MAP_LAYERS}`, {
                params: getParams([{ key: TypeParams.ID, value: id }])
            })
            .then(response => response.data);
    }

    getSatellitialVariablesWithValues(id: number): Promise<RanchVariablesCategorized> {
        return this.http
            .get(`${ItemClaimsUrls.SATELLITAL_VALUES}`, {
                params: getParams([{ key: TypeParams.ID, value: id }])
            })
            .then(response => response.data);
    }

    getStatsForSatellitalChart(
        id: number,
        cancelToken: CancelTokenSource,
        datetimeFrom?: string,
        datetimeTo?: string
    ): Promise<SatellitalChartStatistics> {
        return this.http
            .get(`${ItemClaimsUrls.STATS_SATELLITAL_VALUES}`, {
                cancelToken: cancelToken.token,
                params: getParams([
                    { key: TypeParams.ID, value: id },
                    ...(datetimeFrom ? [{ key: TypeParams.DATE_FROM, value: datetimeFrom }] : []),
                    ...(datetimeTo ? [{ key: TypeParams.DATE_TO, value: datetimeTo }] : [])
                ])
            })
            .then(response => response.data);
    }

    //Zonificar lote
    postZonification(config: ZonificationRequest): Promise<{ message: string; zonified: ZonificationInfo }> {
        return this.http.post(ItemClaimsUrls.ZONIFICATION, config).then(response => response.data);
    }

    //Ndvi Diff
    postNdviDiff(config: NdviDiffRequest): Promise<{ message: string; ndvi: NDVIInfo }> {
        return this.http.post(ItemClaimsUrls.NDVI_DIFF, config).then(response => response.data);
    }
}

export const createZurichApi = (http: AxiosInstance): TenantApi => {
    const zurichApi = new ZurichApi(http);
    return {
        claims: zurichApi,
        itemClaims: zurichApi,
        item: new RanchItemApi(http)
    };
};
